import { type CSSProperties, type RefObject } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar-next';
import Checkbox from '@atlaskit/checkbox-next';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

import { EngineeringAvatar } from './jsm-playground-engineering-avatar';
import {
	ENGINEERING_QUEUE_ROWS,
	type EngineeringWorkflowStatus,
} from './jsm-playground-engineering-data';
import {
	EngineeringPriorityIcon,
	EngineeringRequestTypeIcon,
	EngineeringSlaTag,
} from './jsm-playground-engineering-marks';
import { engineeringMessages as messages } from './jsm-playground-engineering-messages';
import { EngineeringRowStatusDropdown } from './jsm-playground-engineering-status';

type EngineeringQueueProps = {
	firstRowRef: RefObject<HTMLDivElement>;
	onOpenRow: (index: number) => void;
	onStatusChange: (index: number, status: EngineeringWorkflowStatus) => void;
	selectedRowIndex: number | null;
	statusOverrides: Record<number, EngineeringWorkflowStatus>;
	tableRef: RefObject<HTMLDivElement>;
};

const isInteractiveTarget = (target: EventTarget | null) =>
	target instanceof HTMLElement && Boolean(target.closest('button, input, [role="menuitem"]'));

export const JsmPlaygroundEngineeringQueue = ({
	firstRowRef,
	onOpenRow,
	onStatusChange,
	selectedRowIndex,
	statusOverrides,
	tableRef,
}: EngineeringQueueProps) => {
	const intl = useIntl();
	return (
		<div css={tableViewportStyles}>
			<div
				css={tableStyles}
				ref={tableRef}
				role="table"
				aria-label={intl.formatMessage(messages.incomingRequests)}
			>
				<div css={[tableRowStyles, tableHeaderStyles]} role="row">
					<div css={checkboxCellStyles} role="columnheader">
						<Checkbox aria-label={intl.formatMessage(messages.selectAll)} label="" />
					</div>
					<TableHeader width={40} message={messages.type} />
					<TableHeader grow width={168} message={messages.summary} />
					<TableHeader width={180} message={messages.status} />
					<TableHeader width={168} message={messages.timeToResolution} />
					<TableHeader width={140} message={messages.assignee} />
					<TableHeader width={36} message={messages.priority} />
					<TableHeader width={172} message={messages.created} />
				</div>
				{ENGINEERING_QUEUE_ROWS.map((sourceRow, index) => {
					const row = { ...sourceRow, status: statusOverrides[index] ?? sourceRow.status };
					const open = () => onOpenRow(index);
					return (
						<div
							aria-label={intl.formatMessage(messages.openRequest, { summary: row.summary })}
							aria-selected={selectedRowIndex === index}
							key={`${index}-${row.summary}`}
							css={[
								tableRowStyles,
								index === ENGINEERING_QUEUE_ROWS.length - 1 && lastTableRowStyles,
								selectedRowIndex === index && selectedRowStyles,
							]}
							onClick={(event) => {
								if (!isInteractiveTarget(event.target)) open();
							}}
							onKeyDown={(event) => {
								if (
									!isInteractiveTarget(event.target) &&
									(event.key === 'Enter' || event.key === ' ')
								) {
									event.preventDefault();
									open();
								}
							}}
							ref={index === 0 ? firstRowRef : undefined}
							role="row"
							tabIndex={0}
						>
							<div css={checkboxCellStyles} role="cell">
								<Checkbox
									aria-label={intl.formatMessage(messages.selectRequest, { summary: row.summary })}
									label=""
								/>
							</div>
							<div css={typeCellStyles} role="cell">
								<EngineeringRequestTypeIcon size="medium" type={row.requestType} />
							</div>
							<div css={summaryCellStyles} role="cell">
								<span css={summaryTextStyles}>
									{index < 3 ? (
										row.summary
									) : (
										<Skeleton borderRadius={token('radius.xsmall')} height={10} width={140} />
									)}
								</span>
							</div>
							<div css={statusCellStyles} role="cell">
								<EngineeringRowStatusDropdown
									onSelect={(status) => onStatusChange(index, status)}
									showLabel={index < 3}
									status={row.status}
								/>
							</div>
							<div css={slaCellStyles} role="cell">
								<EngineeringSlaTag isLoading={index >= 3} row={row} />
							</div>
							<div css={assigneeCellStyles} role="cell">
								{row.assignee ? (
									<EngineeringAvatar name={row.assignee} />
								) : (
									<Avatar name={intl.formatMessage(messages.unassigned)} size="small" />
								)}
								{index < 3 ? (
									<span css={truncateStyles}>
										{row.assignee ?? intl.formatMessage(messages.unassigned)}
									</span>
								) : (
									<Skeleton borderRadius={token('radius.xsmall')} height={10} width={96} />
								)}
							</div>
							<div css={priorityCellStyles} role="cell">
								<EngineeringPriorityIcon priority={row.priority} />
							</div>
							<div css={createdCellStyles} role="cell">
								<span css={dateTagStyles}>
									<CalendarIcon color={token('color.icon.subtle')} label="" size="small" />
									{index < 3 ? (
										row.created
									) : (
										<Skeleton borderRadius={token('radius.xsmall')} height={10} width={72} />
									)}
								</span>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};

const TableHeader = ({
	grow = false,
	message,
	width,
}: {
	grow?: boolean;
	message: { id: string; defaultMessage: string };
	width: number;
}) => (
	<div
		css={cellStyles}
		role="columnheader"
		style={
			{
				'--cell-width': `${width}px`,
				'--cell-flex': grow ? '1 1 auto' : '0 0 auto',
			} as CSSProperties
		}
	>
		<span css={headerTextStyles}>
			<FormattedMessage {...message} />
		</span>
	</div>
);

// Keeps the region's full height (so a longer queue can still scroll) even though the row is
// top-aligned for the detail panel's sake. Columns past the panel clip rather than squash.
const tableViewportStyles = css({
	minWidth: 0,
	flex: '1 1 auto',
	alignSelf: 'stretch',
	overflowX: 'hidden',
	overflowY: 'auto',
});
const tableStyles = css({
	boxSizing: 'border-box',
	width: '100%',
	minWidth: 0,
	overflow: 'hidden',
	borderWidth: token('border.width', '1px'),
	borderStyle: 'solid',
	borderColor: token('color.border'),
	borderRadius: token('radius.large'),
	backgroundColor: token('elevation.surface'),
});
const tableRowStyles = css({
	display: 'flex',
	alignItems: 'center',
	height: token('space.600'),
	borderBlockEndWidth: token('border.width', '1px'),
	borderBlockEndStyle: 'solid',
	borderBlockEndColor: token('color.border'),
	cursor: 'pointer',
	'&:hover': { backgroundColor: token('color.background.neutral.subtle.hovered') },
	'&:focus-visible': {
		position: 'relative',
		zIndex: 1,
		outlineWidth: token('border.width.focused', '2px'),
		outlineStyle: 'solid',
		outlineColor: token('color.border.focused'),
		outlineOffset: `calc(-1 * ${token('border.width.focused', '2px')})`,
	},
});
const lastTableRowStyles = css({ borderBlockEndWidth: 0 });
const tableHeaderStyles = css({
	backgroundColor: token('elevation.surface.sunken'),
	color: token('color.text.subtle'),
	font: token('font.body'),
	cursor: 'default',
	'&:hover': { backgroundColor: token('elevation.surface.sunken') },
});
const selectedRowStyles = css({
	backgroundColor: token('color.background.selected'),
	'&:hover': { backgroundColor: token('color.background.selected') },
});
const cellStyles = css({
	display: 'flex',
	boxSizing: 'border-box',
	width: 'var(--cell-width)',
	minWidth: 'var(--cell-width)',
	height: '100%',
	flex: 'var(--cell-flex)',
	alignItems: 'center',
	paddingInline: token('space.150'),
	font: token('font.body'),
});
const headerTextStyles = css({
	font: token('font.body'),
	fontWeight: token('font.weight.medium'),
	whiteSpace: 'nowrap',
});
const checkboxCellStyles = css({
	display: 'flex',
	width: token('space.500'),
	minWidth: token('space.500'),
	alignItems: 'center',
	justifyContent: 'center',
});
const typeCellStyles = css({
	display: 'flex',
	width: token('space.500'),
	minWidth: token('space.500'),
	alignItems: 'center',
	justifyContent: 'center',
});
const summaryCellStyles = css({
	display: 'flex',
	minWidth: '168px',
	flex: '1 1 auto',
	alignItems: 'center',
	paddingInline: token('space.150'),
});
const summaryTextStyles = css({
	maxWidth: '100%',
	overflow: 'hidden',
	color: token('color.text'),
	font: token('font.body'),
	textAlign: 'start',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});
const statusCellStyles = css({
	position: 'relative',
	display: 'flex',
	width: '180px',
	minWidth: '180px',
	alignItems: 'center',
	paddingInline: token('space.150'),
	'&:focus-within': { zIndex: 2 },
});
const slaCellStyles = css({
	display: 'flex',
	width: '168px',
	minWidth: '168px',
	alignItems: 'center',
	paddingInline: token('space.150'),
});
const assigneeCellStyles = css({
	display: 'flex',
	width: '140px',
	minWidth: '140px',
	alignItems: 'center',
	gap: token('space.100'),
	paddingInline: token('space.150'),
	font: token('font.body'),
});
const priorityCellStyles = css({
	display: 'flex',
	width: '36px',
	minWidth: '36px',
	alignItems: 'center',
	justifyContent: 'center',
});
const createdCellStyles = css({
	display: 'flex',
	width: '172px',
	minWidth: '172px',
	alignItems: 'center',
	paddingInline: token('space.150'),
});
const truncateStyles = css({ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' });
const dateTagStyles = css({
	display: 'inline-flex',
	height: token('space.300'),
	alignItems: 'center',
	gap: token('space.050'),
	maxWidth: '100%',
	paddingInline: token('space.100'),
	borderWidth: token('border.width', '1px'),
	borderStyle: 'solid',
	borderColor: token('color.border'),
	borderRadius: token('radius.small'),
	font: token('font.body'),
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});
